import React, { useState, useEffect, useCallback } from 'react';
import { quizService, LeadQuizResponsesData } from '../../services/quizService';

interface QuizAnswersSectionProps {
  leadId: number;
}

const DIMENSION_COLORS: Record<string, string> = {
  confidence: '#3B82F6',  // Blue
  pacing: '#8B5CF6',      // Purple
  structure: '#6366F1',   // Indigo
  sensory: '#14B8A6',     // Teal
  interest: '#F97316'     // Orange
};

const DIMENSION_NAMES: Record<string, string> = {
  confidence: 'Emotional Safety',
  pacing: 'Individualized Pacing',
  structure: 'Structure & Predictability',
  sensory: 'Sensory Support',
  interest: 'Interest-Driven Learning'
};

const QuizAnswersSection: React.FC<QuizAnswersSectionProps> = ({ leadId }) => {
  const [quizData, setQuizData] = useState<LeadQuizResponsesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedQuestions, setExpandedQuestions] = useState<Set<number>>(new Set([1])); // Q1 expanded by default

  const loadQuizResponses = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await quizService.getLeadQuizResponses(leadId);
      setQuizData(data);
    } catch (err: any) {
      console.error('Error loading quiz responses:', err);
      setError(err.message || 'Failed to load quiz responses');
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    loadQuizResponses();
  }, [loadQuizResponses]);

  const toggleQuestion = (questionNumber: number) => {
    setExpandedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionNumber)) {
        newSet.delete(questionNumber);
      } else {
        newSet.add(questionNumber);
      }
      return newSet;
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <span className="ml-3 text-gray-600">Loading quiz responses...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="text-center text-gray-500">
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (!quizData || !quizData.quiz_data) {
    return null;
  }

  const { quiz_data } = quizData;
  const dimensionScores = quiz_data.dimension_scores;

  // Get sorted dimensions by percentage
  const sortedDimensions = dimensionScores
    ? Object.entries(dimensionScores.percentages)
        .map(([dim, pct]) => ({
          dimension: dim,
          percentage: pct,
          score: dimensionScores.scores[dim],
          possible: dimensionScores.possible[dim]
        }))
        .sort((a, b) => b.percentage - a.percentage)
    : [];

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-white p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-1">
              {quiz_data.profile?.name || 'Quiz Results'}
            </h3>
            {quiz_data.profile?.tagline && (
              <p className="text-gray-600 text-sm">{quiz_data.profile.tagline}</p>
            )}
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-gray-900">{quiz_data.total_score}</p>
            <p className="text-gray-500 text-sm">Total Score</p>
          </div>
        </div>
        <p className="text-gray-500 text-sm mt-3">
          Completed: {formatDate(quiz_data.completed_at)}
        </p>
      </div>

      {/* Dimension Breakdown */}
      {dimensionScores && (
        <div className="p-6 border-b border-gray-200">
          <h4 className="font-semibold text-gray-900 mb-4">Dimension Breakdown</h4>
          <div className="space-y-3">
            {sortedDimensions.map(({ dimension, percentage, score, possible }, index) => {
              const color = DIMENSION_COLORS[dimension] || '#6B7280';
              const isTop3 = index < 3;

              return (
                <div key={dimension}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-700">
                        {DIMENSION_NAMES[dimension] || dimension}
                      </span>
                      {isTop3 && (
                        <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-medium rounded">
                          Top {index + 1}
                        </span>
                      )}
                    </div>
                    <span className="text-sm text-gray-600">
                      {score}/{possible} ({percentage}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all duration-300"
                      style={{
                        width: `${percentage}%`,
                        backgroundColor: color
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Detailed Answers */}
      <div className="p-6">
        <h4 className="font-semibold text-gray-900 mb-4">Detailed Answers</h4>
        <div className="space-y-3">
          {quiz_data.questions.map((question) => {
            const isExpanded = expandedQuestions.has(question.question_number);
            const isMultiSelect = question.answers.length > 1;

            return (
              <div key={question.question_number} className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleQuestion(question.question_number)}
                  className="w-full p-4 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between text-left"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-gray-900">
                        Q{question.question_number}
                      </span>
                      {isMultiSelect && (
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                          Multi-select ({question.answers.length} selected)
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-700">{question.question_text}</p>
                  </div>
                  <svg
                    className={`w-5 h-5 text-gray-500 transition-transform ${isExpanded ? 'transform rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {isExpanded && (
                  <div className="p-4 bg-white border-t border-gray-200">
                    <div className="space-y-4">
                      {question.answers.map((answer, idx) => (
                        <div key={answer.answer_id} className="pl-4 border-l-2 border-indigo-500">
                          <div className="flex items-start justify-between mb-2">
                            <p className="text-gray-900 font-medium flex-1">
                              {isMultiSelect && <span className="text-indigo-600 mr-2">#{idx + 1}</span>}
                              {answer.answer_text}
                            </p>
                            <span className="ml-3 px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded">
                              {answer.points} pts
                            </span>
                          </div>

                          {answer.dimension_tags && answer.dimension_tags.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {answer.dimension_tags.map((tag) => (
                                <span
                                  key={tag}
                                  className="px-2 py-1 text-xs font-medium rounded"
                                  style={{
                                    backgroundColor: `${DIMENSION_COLORS[tag]}15`,
                                    color: DIMENSION_COLORS[tag]
                                  }}
                                >
                                  {DIMENSION_NAMES[tag] || tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Expand/Collapse All */}
      <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-center gap-3">
        <button
          onClick={() => setExpandedQuestions(new Set(quiz_data.questions.map(q => q.question_number)))}
          className="px-4 py-2 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
        >
          Expand All
        </button>
        <button
          onClick={() => setExpandedQuestions(new Set())}
          className="px-4 py-2 text-sm text-gray-600 hover:text-gray-700 font-medium"
        >
          Collapse All
        </button>
      </div>
    </div>
  );
};

export default QuizAnswersSection;
